import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { ApiError } from '@/api/client'
import { RefreshFailed } from '@/components/RefreshFailed'
import { settingsCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Disclosure } from '@/ui/Disclosure/Disclosure'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { SelectField, TextField } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { workspacesApi } from '../../api/workspaces'
import { MemberList } from '../../components/MemberList'
import { useFeatures } from '../../hooks/queries'
import { useMutationToast } from '../../hooks/useMutationToast'
import {
  ASSIGNABLE_ROLES,
  isOwnRoleRefusal,
  membersKey,
  useWorkspaceMembersQuery,
} from '../../hooks/useWorkspaceMembers'
import { useAuthStore } from '../../store/auth'
import type { WorkspaceInfo, WorkspaceInviteInfo } from '../../types/api'
import { UserModelSection } from '../workspace/UserModelSection'
import styles from './MembersTab.module.css'

/**
 * Shared inline form for the add-member and invite-by-email flows: a text
 * field + role select + submit/cancel. The two flows differ only in copy,
 * input type, and which mutation fires.
 */
function MemberForm({
  value,
  onValue,
  role,
  onRole,
  fieldLabel,
  type = 'text',
  required = false,
  roleLabel,
  submitLabel,
  cancelLabel,
  isPending,
  onSubmit,
  onCancel,
}: {
  value: string
  onValue: (value: string) => void
  role: string
  onRole: (role: string) => void
  fieldLabel: string
  type?: 'text' | 'email'
  required?: boolean
  roleLabel: string
  submitLabel: string
  cancelLabel: string
  isPending: boolean
  onSubmit: (value: string) => void
  onCancel: () => void
}) {
  const fieldId = useId()
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault()
        const trimmed = value.trim()
        if (trimmed) onSubmit(trimmed)
      }}
      onKeyDown={(e) => e.key === 'Escape' && onCancel()}
    >
      <Inline gap="space-8" align="end">
        <div className={styles.grow}>
          <TextField
            id={`${fieldId}-value`}
            label={fieldLabel}
            value={value}
            onValue={onValue}
            inputProps={{ type, required }}
          />
        </div>
        <SelectField
          id={`${fieldId}-role`}
          label={roleLabel}
          value={role}
          onValue={onRole}
        >
          {ASSIGNABLE_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </SelectField>
        <Button type="submit" disabled={!value.trim()} loading={isPending}>
          {submitLabel}
        </Button>
        <Button variant="outline" aria-label={cancelLabel} onClick={onCancel}>
          {settingsCopy.members.cancel}
        </Button>
      </Inline>
    </form>
  )
}

export function MembersTab({ workspace }: { workspace: WorkspaceInfo }) {
  const qc = useQueryClient()
  const features = useFeatures()
  const userId = useAuthStore((s) => s.userId)
  const isSystemAdmin = useAuthStore((s) => s.isSystemAdmin)
  const isUserManaged = (workspace.management_kind ?? 'user') === 'user'
  const isActive = (workspace.lifecycle_status ?? 'active') === 'active'
  const canManage =
    isUserManaged &&
    isActive &&
    (workspace.role === 'owner' || workspace.role === 'admin' || isSystemAdmin)
  const canTransferOwnership =
    isUserManaged && isActive && workspace.role === 'owner'
  const [openModel, setOpenModel] = useState<string | null>(null)

  const membersQuery = useWorkspaceMembersQuery(workspace.workspace_id, {
    staleTime: 30_000,
  })
  const members = membersQuery.data ?? []
  const showMembersSkeleton = useSkeletonDelay(membersQuery.isPending)

  const invitesQuery = useQuery<WorkspaceInviteInfo[]>({
    queryKey: ['workspace-invites', workspace.workspace_id],
    queryFn: () => workspacesApi.listInvites(workspace.workspace_id),
    staleTime: 30_000,
    enabled: canManage,
  })
  const invites = invitesQuery.data ?? []
  const showInvitesSkeleton = useSkeletonDelay(
    canManage && invitesQuery.isPending,
  )

  const [addingUsername, setAddingUsername] = useState('')
  const [addingRole, setAddingRole] = useState('member')
  const [showAddForm, setShowAddForm] = useState(false)

  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('member')

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: membersKey(workspace.workspace_id),
    })

  const invalidateInvites = () =>
    qc.invalidateQueries({
      queryKey: ['workspace-invites', workspace.workspace_id],
    })

  const addMut = useMutationToast({
    mutationFn: ({ username, role }: { username: string; role: string }) =>
      workspacesApi.addMember(workspace.workspace_id, username, role),
    onSuccess: () => {
      void invalidate()
      void qc.invalidateQueries({ queryKey: ['me'] })
      setShowAddForm(false)
      setAddingUsername('')
      setAddingRole('member')
    },
    errorMessage: (error) =>
      error instanceof ApiError && error.status === 404
        ? settingsCopy.members.addNotFound
        : settingsCopy.members.addFailed,
  })

  const updateMut = useMutationToast({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      workspacesApi.updateMember(workspace.workspace_id, userId, role),
    onSuccess: invalidate,
    errorMessage: (error, variables) =>
      isOwnRoleRefusal(error, variables.userId, userId)
        ? settingsCopy.members.roleOwnFailed
        : settingsCopy.members.roleFailed,
  })

  const removeMut = useMutationToast({
    mutationFn: (userId: string) =>
      workspacesApi.removeMember(workspace.workspace_id, userId),
    onSuccess: () => {
      void invalidate()
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
    errorMessage: (error, removedUserId) =>
      isOwnRoleRefusal(error, removedUserId, userId)
        ? settingsCopy.members.removeOwnFailed
        : settingsCopy.members.removeFailed,
  })

  const transferMut = useMutationToast({
    mutationFn: (targetUserId: string) =>
      workspacesApi.transferOwnership(workspace.workspace_id, targetUserId),
    onSuccess: () => {
      void invalidate()
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
    errorMessage: settingsCopy.members.transferFailed,
  })

  const sendInviteMut = useMutationToast({
    mutationFn: ({ email, role }: { email: string; role: string }) =>
      workspacesApi.sendInvite(workspace.workspace_id, email, role),
    onSuccess: () => {
      void invalidateInvites()
      setShowInviteForm(false)
      setInviteEmail('')
      setInviteRole('member')
    },
    successMessage: settingsCopy.invites.sent,
    errorMessage: settingsCopy.invites.failed,
  })

  const revokeInviteMut = useMutationToast({
    mutationFn: (inviteId: string) =>
      workspacesApi.revokeInvite(workspace.workspace_id, inviteId),
    onSuccess: invalidateInvites,
    successMessage: settingsCopy.invites.revoked,
    errorMessage: settingsCopy.invites.revokeFailed,
  })

  return (
    <Stack gap="space-24">
      <section>
        <SectionHeader rule trailingSpacing="section">
          {settingsCopy.members.heading}
        </SectionHeader>
        {membersQuery.isPending ? (
          <div aria-busy="true">
            {showMembersSkeleton && <Skeleton lines={3} />}
          </div>
        ) : membersQuery.isError && !membersQuery.data ? (
          <Alert tone="danger">
            <Inline gap="space-8" align="baseline" wrap>
              <Text>{settingsCopy.loadFailed.members}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void membersQuery.refetch()}
              >
                {settingsCopy.retry}
              </Button>
            </Inline>
          </Alert>
        ) : (
          <Stack gap="space-12">
            {membersQuery.isError && (
              <RefreshFailed
                message={settingsCopy.refreshFailed.members}
                onRetry={() => void membersQuery.refetch()}
              />
            )}
            <MemberList
              members={members}
              canManage={canManage}
              onUpdateRole={(userId, role) =>
                updateMut.mutate({ userId, role })
              }
              onRemove={(userId) => removeMut.mutate(userId)}
              isUpdating={updateMut.isPending}
              isRemoving={removeMut.isPending}
              currentUserId={userId}
              canTransferOwnership={canTransferOwnership}
              onTransferOwnership={(targetUserId) =>
                transferMut.mutate(targetUserId)
              }
              isTransferring={transferMut.isPending}
              footer={
                canManage ? (
                  showAddForm ? (
                    <MemberForm
                      value={addingUsername}
                      onValue={setAddingUsername}
                      role={addingRole}
                      onRole={setAddingRole}
                      fieldLabel={settingsCopy.members.addField}
                      roleLabel={settingsCopy.members.addRole}
                      submitLabel={settingsCopy.members.addSubmit}
                      cancelLabel={settingsCopy.members.cancelAdd}
                      isPending={addMut.isPending}
                      onSubmit={(username) =>
                        addMut.mutate({ username, role: addingRole })
                      }
                      onCancel={() => setShowAddForm(false)}
                    />
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => setShowAddForm(true)}
                    >
                      <Icon name="plus" size="icon-embedded" />
                      {settingsCopy.members.add}
                    </Button>
                  )
                ) : undefined
              }
            />
          </Stack>
        )}
      </section>

      {canManage && (
        <section>
          <SectionHeader rule trailingSpacing="section">
            {settingsCopy.invites.heading}
          </SectionHeader>
          {invitesQuery.isPending ? (
            <div aria-busy="true">
              {showInvitesSkeleton && <Skeleton lines={3} />}
            </div>
          ) : invitesQuery.isError && !invitesQuery.data ? (
            <Alert tone="danger">
              <Inline gap="space-8" align="baseline" wrap>
                <Text>{settingsCopy.loadFailed.invites}</Text>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void invitesQuery.refetch()}
                >
                  {settingsCopy.retry}
                </Button>
              </Inline>
            </Alert>
          ) : (
            <Stack gap="space-12">
              {invitesQuery.isError && (
                <RefreshFailed
                  message={settingsCopy.refreshFailed.invites}
                  onRetry={() => void invitesQuery.refetch()}
                />
              )}
              {invites.length === 0 && (
                <EmptyState
                  variant="inline"
                  description={settingsCopy.invitesEmpty}
                />
              )}
              {(invites.length > 0 || features.email) && (
                <RowList aria-label={settingsCopy.invites.listLabel}>
                  {invites.map((inv) => (
                    <RowListItem key={inv.id}>
                      <Text
                        tone={inv.is_expired ? 'text-ghost' : 'text-sub'}
                        className={styles.name}
                      >
                        {inv.email}
                        {inv.is_expired && (
                          <>
                            {' '}
                            <Text variant="caption" tone="text-ghost">
                              {settingsCopy.invites.expired}
                            </Text>
                          </>
                        )}
                      </Text>
                      <Text variant="caption" tone="text-mute">
                        {inv.role}
                      </Text>
                      <Button
                        variant="ghost"
                        onClick={() => revokeInviteMut.mutate(inv.id)}
                        loading={
                          revokeInviteMut.isPending &&
                          revokeInviteMut.variables === inv.id
                        }
                      >
                        {settingsCopy.invites.revoke}
                      </Button>
                    </RowListItem>
                  ))}
                  {/* Invite-by-email is pointless without an SMTP transport on the
                  server — hide it the same way login hides "forgot password". */}
                  {features.email && (
                    <RowListItem>
                      {showInviteForm ? (
                        <MemberForm
                          type="email"
                          required
                          value={inviteEmail}
                          onValue={setInviteEmail}
                          role={inviteRole}
                          onRole={setInviteRole}
                          fieldLabel={settingsCopy.invites.field}
                          roleLabel={settingsCopy.invites.role}
                          submitLabel={settingsCopy.invites.submit}
                          cancelLabel={settingsCopy.invites.cancel}
                          isPending={sendInviteMut.isPending}
                          onSubmit={(email) =>
                            sendInviteMut.mutate({ email, role: inviteRole })
                          }
                          onCancel={() => setShowInviteForm(false)}
                        />
                      ) : (
                        <Button
                          variant="ghost"
                          onClick={() => setShowInviteForm(true)}
                        >
                          <Icon name="plus" size="icon-embedded" />
                          {settingsCopy.invites.open}
                        </Button>
                      )}
                    </RowListItem>
                  )}
                </RowList>
              )}
            </Stack>
          )}
        </section>
      )}

      {/* Your user model: what workspace agents have recorded about you.
          Read/correct/delete access to your own dossier is the point of the
          feature — it lives beside the member list because it is per-member. */}
      <section>
        <SectionHeader rule trailingSpacing="section">
          {settingsCopy.members.userModel}
        </SectionHeader>
        <UserModelSection wsId={workspace.workspace_id} />
      </section>

      {/* Manager inspection: any member's model, on demand. */}
      {canManage && (
        <section>
          <SectionHeader rule trailingSpacing="section">
            {settingsCopy.members.memberModels}
          </SectionHeader>
          <Stack gap="space-4">
            {members.map((m) => (
              <Disclosure
                key={m.user_id}
                label={m.username}
                count={m.role}
                open={openModel === m.username}
                onOpenChange={(next) => setOpenModel(next ? m.username : null)}
              >
                <UserModelSection
                  wsId={workspace.workspace_id}
                  username={m.username}
                />
              </Disclosure>
            ))}
          </Stack>
        </section>
      )}
    </Stack>
  )
}
